interface FormCondition {
  option: string;
  values: unknown[];
  scope?: { operator?: string; values?: { days?: string } };
}

interface Action {
  function: string;
  operator: string;
  values: number[];
}

interface FormRule {
  scope?: unknown;
  condition: string;
  conditions: FormCondition[];
  actions: Action[];
  [key: string]: unknown;
}

interface FormSpec {
  rules: FormRule[];
  [key: string]: unknown;
}

interface ApiCondition {
  field: string;
  operator: string;
  values: number[];
}

interface ApiSpec {
  lookback_days?: number;
  tag_ids?: number[];
  function?: string;
  condition?: string;
  conditions?: ApiCondition[];
  comment?: string;
}

/** Shape we send back to the API — required fields are always present. */
interface ApiSpecOutput {
  lookback_days: number;
  tag_ids: number[];
  function: string;
  condition: string;
  conditions: ApiCondition[];
  comment?: string;
}

export function apiToTaggingForm(api: ApiSpec) {
  const lookbackDays = String(api.lookback_days ?? 7);

  const conditions: FormCondition[] = (api.conditions ?? []).map((c) => ({
    option: JSON.stringify([c.operator, `\${${c.field}}`]),
    values: c.values,
    scope: { values: { days: lookbackDays } },
  }));

  const actions: Action[] = [
    {
      function: "ADD_TAG",
      operator: "apply",
      values: api.tag_ids?.slice(0, 1) ?? [],
    },
  ];

  return {
    rules: [
      {
        condition: api.condition ?? "all",
        conditions,
        actions,
        ...(api.comment != null && { comment: api.comment }),
      },
    ],
  };
}

export function taggingSpecToApi(spec: FormSpec) {
  const { rules, ...rest } = spec;

  // Tagging programs currently only support a single rule — use the first rule's conditions
  const rule = rules[0];
  const conditions = rule?.conditions ?? [];
  const firstConditionDays = parseInt(conditions[0]?.scope?.values?.days ?? "", 10) || 1;
  // Only a single tag per rule is supported — take the first value.
  const tagIds = (rule?.actions ?? []).flatMap((a) => a.values).slice(0, 1);

  const apiConditions = conditions.map((c) => {
    const [operator, rawField] = JSON.parse(c.option) as [string, string];
    const field = rawField.replaceAll(/^\$\{|\}$/g, "");
    return { field, operator, values: c.values.map(Number) };
  });

  const result: ApiSpecOutput = {
    ...rest,
    lookback_days: firstConditionDays,
    tag_ids: tagIds,
    function: "ADD_TAG",
    condition: rule?.condition ?? "all",
    conditions: apiConditions,
  };

  const comment = (rule as Record<string, unknown>).comment;
  if (typeof comment === "string" && comment !== "") {
    result.comment = comment;
  }

  return result;
}
